import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { closedAppointmentStatuses, Complaint, ComplaintAppointment, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ComplaintService } from '../complaint.service';
import { SyncService, getDocumentAppointments, getSelectedComplaint, getDocumentAttachments } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { logger } from '@icc/shared';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    selector: 'icc-complaint-appointment-delete-dialog',
    templateUrl: './complaint-appointment-delete-dialog.component.html',
    styleUrls: ['./complaint-appointment-delete-dialog.component.scss'],
})
export class ComplaintAppointmentDeleteDialogComponent implements OnInit, OnDestroy {

    /**
     * When true, the dialog is blocked because external changes caused the user
     * to lose delete permissions. User can only close the dialog.
     */
    cannotDelete = false;

    /**
     * Set to true when external permission-affecting change was detected.
     * Once true, every store update will check if user lost permission.
     */
    private hadExternalPermissionChange = false;

    /**
     * Set to true when external appointment status change was detected.
     * Once true, every store update will check if status is now closed.
     */
    private hadExternalAppointmentChange = false;

    /**
     * Set to true when external attachment was added.
     * Once true, every store update will check for delivery note.
     */
    private hadExternalAttachmentChange = false;

    /**
     * Timestamp when the dialog was opened.
     * Used to detect external changes that happened after opening.
     */
    private dialogOpenedAt = 0;

    /**
     * Cached value of whether user has manufacturer permission.
     * Loaded once on init since permissions don't change during dialog lifetime.
     */
    private hasManufacturerPermission = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private complaintService: ComplaintService,
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            appointment: ComplaintAppointment;
            complaint: Complaint;
        },
        private syncService: SyncService,
        private store: Store<State>,
        private permissionsService: NgxPermissionsService,
        private cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit(): Promise<void> {
        this.dialogOpenedAt = Date.now();

        // Cache manufacturer permission - doesn't change during dialog lifetime
        this.hasManufacturerPermission = await this.permissionsService.hasPermission('complaints-manage-manufacturer');

        const editPermissionFields = [
            'assignedTo',
            'distributorStatusId',
            'manufacturerStatusId',
            'authorizedRetailerStatusId',
        ];

        this.subscriptions.push(
            // Subscribe to external changes - mark that external change occurred and check conditions
            this.syncService.externalChanges$.subscribe(({
                appointmentIds,
                documentIds,
                documentFields,
                appointmentFields,
                operationTypes,
                timestamp
            }) => {
                // Ignore changes that happened before dialog was opened
                if (timestamp <= this.dialogOpenedAt) {
                    return;
                }

                // Mark if complaint permission fields were changed externally
                if (documentIds.has(this.data.complaint.uuid)) {
                    const hasRelevantFieldChange = editPermissionFields.some(field =>
                        documentFields.has(field)
                    );
                    if (hasRelevantFieldChange) {
                        logger.log('[DeleteDialog] Permission field changed externally');
                        this.hadExternalPermissionChange = true;
                        // Check immediately with current data (may already be updated by store)
                        if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                            logger.log('[DeleteDialog] BLOCKING - external change removed permission');
                            this.cannotDelete = true;
                            this.cdr.detectChanges();
                        }
                    }
                }

                // Mark if this appointment's status was changed externally
                if (appointmentIds.has(this.data.appointment.uuid) && appointmentFields.has('status')) {
                    logger.log('[DeleteDialog] Appointment status changed externally');
                    this.hadExternalAppointmentChange = true;
                    // Check immediately with current data
                    if (closedAppointmentStatuses.some((status) => status.value === this.data.appointment.status)) {
                        logger.log('[DeleteDialog] BLOCKING - appointment status changed to closed');
                        this.cannotDelete = true;
                        this.cdr.detectChanges();
                    }
                }

                // Mark if attachment was added externally
                if (operationTypes.has('addAttachment')) {
                    logger.log('[DeleteDialog] Attachment added externally');
                    this.hadExternalAttachmentChange = true;
                }
            }),

            // Subscribe to complaint document changes - update data and check if should block
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && complaint.uuid === this.data.complaint.uuid) {
                    Object.assign(this.data.complaint, complaint);

                    // If external permission change was detected, check if user lost permission
                    if (this.hadExternalPermissionChange && !this.cannotDelete) {
                        if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                            logger.log('[DeleteDialog] BLOCKING - external change removed permission');
                            this.cannotDelete = true;
                            this.cdr.detectChanges();
                        }
                    }
                }
            }),

            // Subscribe to appointment changes - update data and check if should block
            this.store.select(getDocumentAppointments(this.data.complaint.uuid)).subscribe((appointments) => {
                const updatedAppointment = appointments.find(
                    (app) => app.uuid === this.data.appointment.uuid
                );
                if (updatedAppointment) {
                    Object.assign(this.data.appointment, updatedAppointment);

                    // If external appointment change was detected, check if status is closed
                    if (this.hadExternalAppointmentChange && !this.cannotDelete) {
                        if (closedAppointmentStatuses.some((status) => status.value === updatedAppointment.status)) {
                            logger.log('[DeleteDialog] BLOCKING - appointment status changed to closed');
                            this.cannotDelete = true;
                            this.cdr.detectChanges();
                        }
                    }
                }
            }),

            // Subscribe to attachments - check if delivery note was added
            this.store.select(getDocumentAttachments(this.data.complaint.uuid)).subscribe((attachments) => {
                // If external attachment was added, check for delivery note
                if (this.hadExternalAttachmentChange && !this.cannotDelete) {
                    const hasDeliveryNote = attachments.some(
                        (att) => att.appointmentId === this.data.appointment.uuid
                            && att.attachmentType === 'appointmentDeliveryNote'
                    );
                    if (hasDeliveryNote) {
                        logger.log('[DeleteDialog] BLOCKING - delivery note was added');
                        this.cannotDelete = true;
                        this.cdr.detectChanges();
                    }
                }
            }),
        );
    }

    /**
     * Checks if the current status allows editing.
     */
    private currentStatusAllowEdit(): boolean {
        const userType = this.complaintService.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.complaintService.statuses.find((s) => s.id === statusId);
        return status?.allowEdit ?? false;
    }

    /**
     * Checks if the current user is the assigned party or has manufacturer permission.
     * Users with 'complaints-manage-manufacturer' permission bypass the assignment check.
     */
    private isUserAssigned(): boolean {
        if (this.hasManufacturerPermission) {
            return true;
        }
        const userType = this.complaintService.getUserType();
        return this.data.complaint.assignedTo?.startsWith(userType) ?? false;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    removeAppointment() {
        this.complaintService.removeAppointment(this.data.appointment);
        this.close();
    }

    close() {
        this.dialogRef.close();
    }
}
